import type { CanvasObject, CanvasObjectType, SectionCanvasObject } from "./canvas-object";
import { frameAABB } from "./connector-geometry";

// セクション（領域コンテナ）のドメイン定数と、移動・複製の連動対象を決める包含・重なり判定ロジック。
// 親子関係は永続化せず、「ドラッグ移動で一緒に動かすオブジェクト」（前面かつ正の面積で重なるもの。
// セクションどうしは完全に含まれる入れ子のもの）と
// 「セクションの Alt+ドラッグ複製で一緒に複製するオブジェクト」（矩形に完全に含まれるもの）を、
// ドラッグ開始時にこの判定で幾何計算する（サーバスキーマ・移動 op の変更が不要で、
// 既存のクライアント駆動グループ移動と整合するため）。

/** クリック配置時の既定サイズ（幅）。 */
export const SECTION_DEFAULT_W = 960;
/** クリック配置時の既定サイズ（高さ）。 */
export const SECTION_DEFAULT_H = 540;
/** リサイズ・ドラッグ配置時の最小サイズ（幅）。タイトルバーが潰れない下限として設定。 */
export const SECTION_MIN_W = 200;
/** リサイズ・ドラッグ配置時の最小サイズ（高さ）。 */
export const SECTION_MIN_H = 120;
/** fill 未設定時に描画へ使う既定の背景色（薄いグレー。塗りはレンダラ側で半透明化する）。 */
export const SECTION_DEFAULT_FILL = "#8993a4";

/** 作成時の既定タイトル。 */
export const SECTION_DEFAULT_TITLE = "セクション";

/**
 * セクションの Alt+ドラッグ複製で一緒に複製する内容物（矩形に完全に含まれるもの）の id を返す。
 *
 * 通常の移動で一緒に動かす対象は {@link collectObjectsCarriedByMove}（前面かつ正の面積の重なり。
 * セクションどうしは完全に含まれる入れ子のもの）で決める。複製へ重なりの規則を使うと、セクションから
 * 少しはみ出した付箋まで複製されて増えるため、複製は従来どおりこの完全包含の規則に限る。
 *
 * 判定規則（設計文書 §3-6）:
 * - comment-thread は保存 x/y をアンカー点とし、その点がセクション矩形に含まれれば対象にする。
 * - comment-thread 以外は AABB（x/y/w/h）がセクション矩形に**完全に含まれる**こと。
 *   端に重なっただけのオブジェクトを巻き込まず、視覚的な「中に入っている」と一致させる。
 * - connector は端点追従に委ねるため対象外。
 * - locked のオブジェクトは追従しない（ロック＝位置固定の意図を優先する）。
 * - セクション自身は対象外。ネストされた別セクションは完全包含なら追従する。
 *
 * 回転は考慮しない（セクションは回転非対応。内容物が回転していても保存 AABB は
 * x/y/w/h のまま
 * であり、既存のグループ移動・マーキーと同じ近似に揃える）。
 */
export function collectSectionContainedObjectIds(
  objects: Iterable<CanvasObject>,
  section: SectionCanvasObject
): string[] {
  const result: string[] = [];
  for (const object of objects) {
    if (object.id === section.id) continue;
    // connector は端点追従に委ねるため対象外。comment-thread（ピン）は、アンカー（x/y）が
    // 内側にあれば内容物として追従させる（判定規則は collectObjectsCarriedByMove 参照）。
    if (object.type === "connector") continue;
    if (object.locked === true) continue;
    if (isCarriedWithin(object, section)) {
      result.push(object.id);
    }
  }
  return result;
}

/**
 * 通常のドラッグ移動で、mover と一緒に動かすオブジェクトの id を返す（セクション挙動の全オブジェクトへの一般化）。
 *
 * mover の外接矩形と**正の面積で一部でも重なり**、かつ mover **より前面（描画順で後ろ＝上に載っている）**
 * に描かれるオブジェクトを一緒に動かす。完全に収まっている必要はなく、付箋から少しはみ出したスタンプも
 * 背面の付箋の移動に追従する。
 *
 * 判定規則:
 * - 重なりは外接矩形（AABB）どうしの共通部分が縦横とも正の長さを持つこと（{@link isAabbOverlapping}）。
 *   辺や角が接するだけは対象外。画面の拡大率による閾値は設けない（ステージ座標で判定する）。
 * - 外接矩形は {@link carryAabbOf} で求める。回転しているオブジェクト（mover を含む）は回転後の 4 隅から
 *   求め、回転前の枠だけでは判定しない。透過画像・円・矢印などの内部の形までは見ない（図形の枠どうしの重なり）。
 * - 前面判定は描画順の正本 {@link compareCanvasObjectsForRender}（セクション帯 → zOrder → id）に従う。
 *   mover より背面（下）にあるオブジェクトは重なっていても対象外（置いていかれる）。セクションを動かす場合、
 *   通常オブジェクトは常にセクションより前面に描かれるため、はみ出していても重なっていれば一緒に動く。
 * - セクションどうしは描画順を比べず、外接矩形が mover の外接矩形に完全に含まれる入れ子のセクションだけを
 *   対象にする（境界上も含む）。新規作成は最前面に置かれるため、先に作った小さいセクションを後から作った
 *   大きいセクションで囲むと、外側が描画順で上になる。描画順で決めると、外側を動かしても内側の枠だけが
 *   残って中身と離れ、内側を動かすと外側の大きなセクションまで動いてしまう。端だけが重なる隣のセクションや、
 *   外側・背面の大きなセクションは巻き込まない。
 * - comment-thread は保存 x/y のアンカー点が mover の外接矩形に含まれれば対象にする（境界上も含む）。
 *   専用オーバーレイで常に前面に描かれるため、保存描画順は比較しない。吹き出しの大きさも判定に使わない。
 * - connector は端点追従に委ねるため対象外。
 * - locked のオブジェクトは追従しない（ロック＝位置固定の意図を優先する）。
 * - mover 自身は対象外。
 * - 枠が不正（x / y / w / h・回転角が有限でない、幅または高さが 0 以下）なオブジェクトは対象外。
 *   mover の枠が不正なら何も運ばない。
 * - mover が comment-thread の場合は、他のオブジェクトを運ばない。
 * - 対象と判定したオブジェクトを起点に、さらに重なりを辿ることはしない。呼び出し側は直接動かす
 *   オブジェクトごとにこの関数を 1 回だけ呼び、結果の和集合を取る（触れ合った図形の連鎖移動を防ぐ）。
 *
 * Alt+ドラッグの複製で一緒に複製する内容物は、この関数ではなく {@link collectSectionContainedObjectIds}
 * （セクションへの完全包含）で決める。
 */
export function collectObjectsCarriedByMove(
  objects: Iterable<CanvasObject>,
  mover: CanvasObject
): string[] {
  // コメントピンは面を持たないアンカー点であり、他オブジェクトを運ぶ主体にはならない
  // （同座標の別ピンだけが点包含になり得るが、ピンがピンを運ぶ挙動は意図しない）。
  if (mover.type === "comment-thread") return [];
  const moverBox = carryAabbOf(mover);
  if (!moverBox) return [];
  const result: string[] = [];
  for (const object of objects) {
    if (object.id === mover.id) continue;
    if (object.type === "connector") continue;
    if (object.locked === true) continue;
    if (object.type === "comment-thread") {
      // コメントピンは専用オーバーレイ層で常に全オブジェクトより前面に描かれるため、保存 zOrder に
      // よる前面判定を適用しない。適用すると「図形より先に作ったコメントは追従しない」という、
      // 画面から読み取れない基準（作成順）で追従の有無が変わってしまう。
      if (
        Number.isFinite(object.x) &&
        Number.isFinite(object.y) &&
        isPointContainedInAabb(object, moverBox)
      ) {
        result.push(object.id);
      }
      continue;
    }
    if (mover.type === "section" && object.type === "section") {
      // セクションどうしは、描画順に関わらず完全に含まれる入れ子のセクションだけを運ぶ（理由は関数の説明）。
      const nestedBox = carryAabbOf(object);
      if (nestedBox && isAabbContained(nestedBox, moverBox)) {
        result.push(object.id);
      }
      continue;
    }
    if (compareCanvasObjectsForRender(mover, object) >= 0) continue;
    const box = carryAabbOf(object);
    if (box && isAabbOverlapping(box, moverBox)) {
      result.push(object.id);
    }
  }
  return result;
}

/**
 * 描画バンド。セクションは通常オブジェクトより常に背面に描画するため、
 * zOrder 比較の前段でバンド（0 = セクション帯 / 1 = 通常帯）を比較する。
 * Web レンダリング（サブレイヤ分離）とサムネイル / PDF（描画順）で同じ規則を共有する。
 */
export function canvasObjectRenderBand(type: CanvasObjectType): 0 | 1 {
  return type === "section" ? 0 : 1;
}

/**
 * 描画順の比較関数。バンド（セクション帯が先）→ zOrder → id（同値時の安定化）の順で比較する。
 */
export function compareCanvasObjectsForRender(a: CanvasObject, b: CanvasObject): number {
  const bandDiff = canvasObjectRenderBand(a.type) - canvasObjectRenderBand(b.type);
  if (bandDiff !== 0) return bandDiff;
  if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

interface Aabb {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * point が outer の AABB の内側にあるか（境界一致は「内側」とみなす）。
 *
 * コメントピン（comment-thread）の追従判定に使う。ピンは保存 x/y をアンカー（配置クリック位置）
 * とし、w/h はビューポート固定表示のための名目値でしかないため、矩形どうしの isAabbContained では
 * 「右端・下端から名目サイズ分の帯にアンカーがあると内包扱いにならない」ずれが出る。判定を点で
 * 行うことで、web 側の名目サイズ定数から domain の規則を独立させる。
 */
export function isPointContainedInAabb(point: { x: number; y: number }, outer: Aabb): boolean {
  return (
    point.x >= outer.x &&
    point.y >= outer.y &&
    point.x <= outer.x + outer.w &&
    point.y <= outer.y + outer.h
  );
}

/**
 * 完全包含判定の本体（{@link collectSectionContainedObjectIds} 用）。comment-thread はアンカー点、
 * それ以外は AABB の完全包含で判定する。
 */
function isCarriedWithin(object: CanvasObject, container: CanvasObject): boolean {
  return object.type === "comment-thread"
    ? isPointContainedInAabb(object, container)
    : isAabbContained(object, container);
}

/**
 * セクションは回転非対応（設計判断 #8）。保存前にセクションの rotation を取り除く。
 * transform 経路（op-applier / canvas-object-service）では rotation を strip しているが、
 * create 経路では範囲チェックしかしないため、細工クライアントが `type: "section", rotation: 45`
 * を送ると回転値が永続化され、本体は無回転表示なのに選択枠・PDF・サムネイルだけ回転する
 * 不整合になり得る。これを防ぐため、create 経路（WS / REST / MCP の prepareObjectForCreate）で
 * この関数を通す。section 以外・rotation 未設定はそのまま返す。
 */
export function stripSectionRotation(object: CanvasObject): CanvasObject {
  if (object.type !== "section" || object.rotation === undefined) return object;
  const { rotation: _ignoredRotation, ...rest } = object;
  return rest;
}

/** inner の AABB が outer の AABB に完全に含まれるか（境界一致は「含まれる」とみなす）。 */
export function isAabbContained(inner: Aabb, outer: Aabb): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}

/**
 * a と b の AABB が正の面積で重なるか（移動の連動判定用）。
 *
 * 共通部分の幅と高さがともに正のときだけ true を返す。辺や角が接するだけ（共通部分の幅か高さが 0）は
 * false。小数座標でも、共通部分が正であれば 1px 未満でも true になる。
 */
export function isAabbOverlapping(a: Aabb, b: Aabb): boolean {
  return (
    Math.min(a.x + a.w, b.x + b.w) > Math.max(a.x, b.x) &&
    Math.min(a.y + a.h, b.y + b.h) > Math.max(a.y, b.y)
  );
}

/**
 * 移動の連動判定に使う、オブジェクトのステージ座標での外接矩形（AABB）を返す。枠が不正なら null。
 *
 * - x / y / w / h のいずれかが有限でない、または幅・高さが 0 以下なら null（連動判定の対象外）。
 * - 回転がある場合は、中心まわりに回転させた 4 隅の外接矩形を返す（描画の CSS rotate と同じ時計回り）。
 *   90 度の倍数は、三角関数の丸め誤差で「辺が接するだけ」が「重なる」に変わらないよう厳密な値で返す。
 * - 回転角が有限でない場合も null。
 */
export function carryAabbOf(
  object: Aabb & { readonly rotation?: number | undefined }
): Aabb | null {
  const { x, y, w, h } = object;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h)) {
    return null;
  }
  if (w <= 0 || h <= 0) return null;
  const rotation = object.rotation ?? 0;
  if (!Number.isFinite(rotation)) return null;
  const quarterTurns = rotation / 90;
  if (Number.isInteger(quarterTurns)) {
    // 180 度の倍数は元の枠と同じ。90 度の奇数倍は中心を保ったまま幅と高さが入れ替わる。
    return quarterTurns % 2 === 0
      ? { x, y, w, h }
      : { x: x + (w - h) / 2, y: y + (h - w) / 2, w: h, h: w };
  }
  return frameAABB({ x, y, w, h, rotation });
}
